import { create } from "zustand"
import {
  ExportMapVariable,
  ExportMapGroup,
  ExportMapProfile,
  ExportMapCategory,
} from "src/types/exportMap.types"
import { ExportMapFillRow } from "src/types/sourceCodes.types"
import {
  buildFillRowIndex,
  fillVariable,
} from "features/QueryConsole/components/ExportMap/utils/exportMapFill"

/**
 * State interface for the export map store
 */
interface ExportMapState {
  /** Hierarchical groups of variables (for nested // paths) */
  groups: ExportMapGroup[]
  /** Root-level variables (without //) displayed directly */
  rootVariables: ExportMapVariable[]
  /** Flat list of all new variables (from manipulations) */
  newVariables: ExportMapVariable[]
  /** Flat list of all variables for easy lookup */
  allVariables: ExportMapVariable[]
  /** Map of uppercase code -> array of fullLabels for O(1) duplicate detection */
  codeToLabelsMap: Map<string, string[]>
  /** Currently selected profile */
  selectedProfile: ExportMapProfile | null
  /** Available profiles for the project */
  availableProfiles: ExportMapProfile[]
  /** Whether there are unsaved changes */
  hasChanges: boolean
  /** Whether the map is currently loading */
  isLoading: boolean
  /** Search/filter term */
  searchTerm: string
  /** Generated CSV content (from engine) */
  originalCsv: string
  /** Original map.csv content (from imported data) */
  originalMapCsv: string
  /** Whether to use original codes from map.csv (true) or auto-generated codes (false) */
  useOriginalCodes: boolean
  /** Set of group paths (joined with //) where max-multi is enabled */
  maxMultiGroups: Set<string>
}

/**
 * Actions interface for the export map store
 */
interface ExportMapActions {
  // Data setters
  setVariables: (variables: ExportMapVariable[]) => void
  setGroups: (groups: ExportMapGroup[]) => void
  setRootVariables: (variables: ExportMapVariable[]) => void
  setAvailableProfiles: (profiles: ExportMapProfile[]) => void
  setSelectedProfile: (profile: ExportMapProfile | null) => void
  setOriginalCsv: (csv: string) => void
  setOriginalMapCsv: (csv: string) => void

  // Variable updates
  updateVariableCode: (fullLabel: string, newCode: string) => void
  updateCategoryCode: (
    fullLabel: string,
    categoryLabel: string,
    newCode: number | string
  ) => void

  // Group operations
  toggleGroupExpanded: (groupPath: string[]) => void
  expandAllGroups: () => void
  collapseAllGroups: () => void

  // UI state
  setSearchTerm: (term: string) => void
  setIsLoading: (loading: boolean) => void
  setHasChanges: (hasChanges: boolean) => void
  setUseOriginalCodes: (useOriginal: boolean) => void

  // Getters
  getVariableByLabel: (fullLabel: string) => ExportMapVariable | undefined
  getFilteredVariables: () => ExportMapVariable[]
  getFilteredNewVariables: () => ExportMapVariable[]
  /** Check if a code is used by another variable (excluding the specified variable) */
  isDuplicateCode: (code: string, excludeFullLabel: string) => boolean
  /** Get all duplicate codes and their labels */
  getDuplicateCodes: () => Map<string, string[]>

  // Bulk operations
  /** Apply prefix and/or suffix to variable codes for specified variables */
  bulkUpdateVariablePrefix: (
    variableLabels: string[],
    prefix: string,
    suffix: string
  ) => number
  /** Find categories matching pattern and set their codes */
  bulkSetCategoryCode: (
    searchPattern: string,
    newCode: number | string,
    caseInsensitive: boolean,
    exactMatch?: boolean
  ) => { variableCount: number; categoryCount: number }
  /** Find max-multi BOOL fields by fullLabel and set their maxMultiCode */
  bulkSetMaxMultiCode: (
    searchPattern: string,
    newCode: number,
    caseInsensitive: boolean,
    exactMatch?: boolean
  ) => { variableCount: number }
  /**
   * Apply variable and category codes proposed by "Fill from source document".
   * Rows match variables by fullLabel and categories by label.
   * @param onlyEmpty - Only set variable codes that are currently empty
   */
  applyFilledCodes: (
    rows: ExportMapFillRow[],
    onlyEmpty?: boolean
  ) => { variableCount: number; categoryCount: number }

  // Max-multi
  /** Toggle max-multi mode for a group of BOOL variables */
  toggleMaxMulti: (groupPath: string[]) => void
  /** Toggle max-multi for ALL groups that contain BOOL variables */
  toggleMaxMultiAll: (enable: boolean) => void
  /** Update the max-multi code for a single BOOL variable */
  updateMaxMultiCode: (fullLabel: string, code: number | undefined) => void
  /** Check if a group has max-multi enabled */
  isMaxMultiGroup: (groupPath: string[]) => boolean
  /** Check if all BOOL groups have max-multi enabled */
  isAllMaxMulti: () => boolean
  /** Get validation errors for max-multi groups: groupPath -> error messages */
  getMaxMultiValidationErrors: () => Map<string, string[]>

  // Reset
  reset: () => void
}

type ExportMapStore = ExportMapState & ExportMapActions

/**
 * Builds a Map of uppercase code -> array of fullLabels for O(1) duplicate detection
 */
const buildCodeToLabelsMap = (
  variables: ExportMapVariable[]
): Map<string, string[]> => {
  const map = new Map<string, string[]>()
  for (const v of variables) {
    const code = v.variableCode.toUpperCase()
    if (!code) continue
    const existing = map.get(code) || []
    existing.push(v.fullLabel)
    map.set(code, existing)
  }
  return map
}

const initialState: ExportMapState = {
  groups: [],
  rootVariables: [],
  newVariables: [],
  allVariables: [],
  codeToLabelsMap: new Map(),
  selectedProfile: null,
  availableProfiles: [],
  hasChanges: false,
  isLoading: false,
  searchTerm: "",
  originalCsv: "",
  originalMapCsv: "",
  useOriginalCodes: true,
  maxMultiGroups: new Set<string>(),
}

/**
 * Recursively updates expanded state for all groups
 */
const setAllGroupsExpanded = (
  groups: ExportMapGroup[],
  expanded: boolean
): ExportMapGroup[] => {
  return groups.map((group) => ({
    ...group,
    isExpanded: expanded,
    subgroups: setAllGroupsExpanded(group.subgroups, expanded),
  }))
}

/**
 * Recursively finds and toggles a group's expanded state
 */
const toggleGroupInTree = (
  groups: ExportMapGroup[],
  targetPath: string[],
  currentPath: string[] = []
): ExportMapGroup[] => {
  return groups.map((group) => {
    const fullPath = [...currentPath, group.label]
    const isTarget =
      fullPath.length === targetPath.length &&
      fullPath.every((p, i) => p === targetPath[i])

    if (isTarget) {
      return { ...group, isExpanded: !group.isExpanded }
    }

    if (
      fullPath.length < targetPath.length &&
      fullPath.every((p, i) => p === targetPath[i])
    ) {
      return {
        ...group,
        subgroups: toggleGroupInTree(group.subgroups, targetPath, fullPath),
      }
    }

    return group
  })
}

/**
 * Zustand store for managing export map state during editing.
 *
 * Handles variable codes, category codes, profile selection, and UI state.
 */
export const useExportMapStore = create<ExportMapStore>((set, get) => ({
  ...initialState,

  // Data setters
  setVariables: (variables: ExportMapVariable[]) => {
    const newVars = variables.filter((v) => v.isNew)

    // Auto-detect max-multi groups from loaded variables
    const detectedGroups = new Set<string>()
    for (const v of variables) {
      if (v.type === "BOOL" && v.maxMultiCode !== undefined && v.path.length >= 2) {
        const groupKey = v.path.slice(0, -1).join("//")
        detectedGroups.add(groupKey)
      }
    }

    set({
      allVariables: variables,
      newVariables: newVars,
      codeToLabelsMap: buildCodeToLabelsMap(variables),
      maxMultiGroups: detectedGroups,
      hasChanges: false,
    })
  },

  setGroups: (groups: ExportMapGroup[]) => set({ groups }),

  setRootVariables: (variables: ExportMapVariable[]) => set({ rootVariables: variables }),

  setAvailableProfiles: (profiles: ExportMapProfile[]) =>
    set({ availableProfiles: profiles }),

  setSelectedProfile: (profile: ExportMapProfile | null) =>
    set({ selectedProfile: profile }),

  setOriginalCsv: (csv: string) => set({ originalCsv: csv }),

  setOriginalMapCsv: (csv: string) => set({ originalMapCsv: csv }),

  // Variable updates
  updateVariableCode: (fullLabel: string, newCode: string) => {
    set((state) => {
      // Find the old code to update the map
      const oldVariable = state.allVariables.find((v) => v.fullLabel === fullLabel)
      const oldCode = oldVariable?.variableCode.toUpperCase() || ""
      const newCodeUpper = newCode.toUpperCase()

      // Update the codeToLabelsMap incrementally
      const newMap = new Map(state.codeToLabelsMap)

      // Remove from old code's list
      if (oldCode) {
        const oldLabels = newMap.get(oldCode) || []
        const filteredLabels = oldLabels.filter((l) => l !== fullLabel)
        if (filteredLabels.length === 0) {
          newMap.delete(oldCode)
        } else {
          newMap.set(oldCode, filteredLabels)
        }
      }

      // Add to new code's list
      if (newCodeUpper) {
        const newLabels = newMap.get(newCodeUpper) || []
        newLabels.push(fullLabel)
        newMap.set(newCodeUpper, newLabels)
      }

      return {
        allVariables: state.allVariables.map((v) =>
          v.fullLabel === fullLabel ? { ...v, variableCode: newCode } : v
        ),
        newVariables: state.newVariables.map((v) =>
          v.fullLabel === fullLabel ? { ...v, variableCode: newCode } : v
        ),
        codeToLabelsMap: newMap,
        hasChanges: true,
      }
    })
  },

  updateCategoryCode: (
    fullLabel: string,
    categoryLabel: string,
    newCode: number | string
  ) => {
    const updateCategories = (
      categories: ExportMapCategory[] | undefined
    ): ExportMapCategory[] | undefined => {
      if (!categories) return undefined
      return categories.map((cat) =>
        cat.label === categoryLabel ? { ...cat, code: newCode } : cat
      )
    }

    set((state) => ({
      allVariables: state.allVariables.map((v) =>
        v.fullLabel === fullLabel
          ? { ...v, categories: updateCategories(v.categories) }
          : v
      ),
      newVariables: state.newVariables.map((v) =>
        v.fullLabel === fullLabel
          ? { ...v, categories: updateCategories(v.categories) }
          : v
      ),
      hasChanges: true,
    }))
  },

  // Group operations
  toggleGroupExpanded: (groupPath: string[]) => {
    set((state) => ({
      groups: toggleGroupInTree(state.groups, groupPath),
    }))
  },

  expandAllGroups: () => {
    set((state) => ({
      groups: setAllGroupsExpanded(state.groups, true),
    }))
  },

  collapseAllGroups: () => {
    set((state) => ({
      groups: setAllGroupsExpanded(state.groups, false),
    }))
  },

  // UI state
  setSearchTerm: (term: string) => set({ searchTerm: term }),

  setIsLoading: (loading: boolean) => set({ isLoading: loading }),

  setHasChanges: (hasChanges: boolean) => set({ hasChanges }),

  setUseOriginalCodes: (useOriginal: boolean) => set({ useOriginalCodes: useOriginal }),

  // Getters
  getVariableByLabel: (fullLabel: string) => {
    return get().allVariables.find((v) => v.fullLabel === fullLabel)
  },

  getFilteredVariables: () => {
    const { allVariables, searchTerm } = get()
    if (!searchTerm.trim()) return allVariables

    const term = searchTerm.toLowerCase()
    return allVariables.filter(
      (v) =>
        v.fullLabel.toLowerCase().includes(term) ||
        v.variableCode.toLowerCase().includes(term)
    )
  },

  getFilteredNewVariables: () => {
    const { newVariables, searchTerm } = get()
    if (!searchTerm.trim()) return newVariables

    const term = searchTerm.toLowerCase()
    return newVariables.filter(
      (v) =>
        v.fullLabel.toLowerCase().includes(term) ||
        v.variableCode.toLowerCase().includes(term)
    )
  },

  isDuplicateCode: (code: string, excludeFullLabel: string) => {
    if (!code.trim()) return false
    const { codeToLabelsMap } = get()
    const upperCode = code.toUpperCase()
    const labels = codeToLabelsMap.get(upperCode)
    if (!labels) return false
    // Check if there are other labels besides the excluded one
    return labels.some((l) => l !== excludeFullLabel)
  },

  getDuplicateCodes: () => {
    const { allVariables } = get()
    const codeToLabels = new Map<string, string[]>()

    for (const v of allVariables) {
      const code = v.variableCode.toUpperCase()
      const existing = codeToLabels.get(code) || []
      existing.push(v.fullLabel)
      codeToLabels.set(code, existing)
    }

    // Filter to only duplicates
    const duplicates = new Map<string, string[]>()
    for (const [code, labels] of codeToLabels) {
      if (labels.length > 1) {
        duplicates.set(code, labels)
      }
    }

    return duplicates
  },

  // Bulk operations
  bulkUpdateVariablePrefix: (
    variableLabels: string[],
    prefix: string,
    suffix: string
  ) => {
    const labelsSet = new Set(variableLabels)
    let updatedCount = 0

    const updateVariableCode = (v: ExportMapVariable): ExportMapVariable => {
      if (labelsSet.has(v.fullLabel)) {
        updatedCount++
        return {
          ...v,
          variableCode: `${prefix}${v.variableCode}${suffix}`,
        }
      }
      return v
    }

    const updateGroupCodes = (group: ExportMapGroup): ExportMapGroup => {
      return {
        ...group,
        variables: group.variables.map(updateVariableCode),
        subgroups: group.subgroups.map(updateGroupCodes),
      }
    }

    set((state) => {
      const updatedAllVariables = state.allVariables.map(updateVariableCode)

      // Reset count for newVariables pass (avoid double counting)
      const prevCount = updatedCount

      const updatedNewVariables = state.newVariables.map((v) => {
        if (labelsSet.has(v.fullLabel)) {
          return {
            ...v,
            variableCode: `${prefix}${v.variableCode}${suffix}`,
          }
        }
        return v
      })

      // Update groups and rootVariables for UI refresh
      const updatedGroups = state.groups.map(updateGroupCodes)
      const updatedRootVariables = state.rootVariables.map(updateVariableCode)

      // Use count from allVariables pass
      updatedCount = prevCount

      return {
        allVariables: updatedAllVariables,
        newVariables: updatedNewVariables,
        groups: updatedGroups,
        rootVariables: updatedRootVariables,
        codeToLabelsMap: buildCodeToLabelsMap(updatedAllVariables),
        hasChanges: updatedCount > 0 ? true : state.hasChanges,
      }
    })

    return updatedCount
  },

  bulkSetCategoryCode: (
    searchPattern: string,
    newCode: number | string,
    caseInsensitive: boolean,
    exactMatch: boolean = false
  ) => {
    let variableCount = 0
    let categoryCount = 0
    const matchedVariables = new Set<string>()

    const matchesPattern = (label: string) => {
      const labelToMatch = caseInsensitive ? label.toLowerCase() : label
      const patternToMatch = caseInsensitive ? searchPattern.toLowerCase() : searchPattern

      if (exactMatch) {
        return labelToMatch === patternToMatch
      }
      return labelToMatch.includes(patternToMatch)
    }

    const updateVariableCategories = (v: ExportMapVariable): ExportMapVariable => {
      if (!v.categories || v.categories.length === 0) return v

      let variableModified = false
      const updatedCategories = v.categories.map((cat) => {
        if (matchesPattern(cat.label)) {
          if (!variableModified) {
            variableModified = true
            matchedVariables.add(v.fullLabel)
          }
          categoryCount++
          return { ...cat, code: newCode }
        }
        return cat
      })

      if (variableModified) {
        return { ...v, categories: updatedCategories }
      }
      return v
    }

    const updateGroupCategories = (group: ExportMapGroup): ExportMapGroup => {
      return {
        ...group,
        variables: group.variables.map(updateVariableCategories),
        subgroups: group.subgroups.map(updateGroupCategories),
      }
    }

    set((state) => {
      const updatedAllVariables = state.allVariables.map(updateVariableCategories)

      // Reset counters to avoid double counting, then count again for newVariables
      const prevCategoryCount = categoryCount

      const updatedNewVariables = state.newVariables.map((v) => {
        if (!v.categories || v.categories.length === 0) return v

        const updatedCategories = v.categories.map((cat) => {
          if (matchesPattern(cat.label)) {
            return { ...cat, code: newCode }
          }
          return cat
        })

        if (matchedVariables.has(v.fullLabel)) {
          return { ...v, categories: updatedCategories }
        }
        return v
      })

      // Update groups and rootVariables for UI refresh
      const updatedGroups = state.groups.map(updateGroupCategories)
      const updatedRootVariables = state.rootVariables.map(updateVariableCategories)

      variableCount = matchedVariables.size
      // Use the count from allVariables (first pass)
      categoryCount = prevCategoryCount

      return {
        allVariables: updatedAllVariables,
        newVariables: updatedNewVariables,
        groups: updatedGroups,
        rootVariables: updatedRootVariables,
        hasChanges: categoryCount > 0 ? true : state.hasChanges,
      }
    })

    return { variableCount, categoryCount }
  },

  bulkSetMaxMultiCode: (
    searchPattern: string,
    newCode: number,
    caseInsensitive: boolean,
    exactMatch: boolean = false
  ) => {
    const matchedLabels = new Set<string>()

    const pattern = caseInsensitive ? searchPattern.toLowerCase() : searchPattern

    const isMatch = (v: ExportMapVariable): boolean => {
      if (v.type !== "BOOL" || v.maxMultiCode === undefined) return false
      const label = caseInsensitive ? v.fullLabel.toLowerCase() : v.fullLabel
      return exactMatch ? label === pattern : label.includes(pattern)
    }

    set((state) => {
      for (const v of state.allVariables) {
        if (isMatch(v)) matchedLabels.add(v.fullLabel)
      }

      if (matchedLabels.size === 0) {
        return state
      }

      const updateVar = (v: ExportMapVariable): ExportMapVariable =>
        matchedLabels.has(v.fullLabel) ? { ...v, maxMultiCode: newCode } : v

      const updateGroupVars = (group: ExportMapGroup): ExportMapGroup => ({
        ...group,
        variables: group.variables.map(updateVar),
        subgroups: group.subgroups.map(updateGroupVars),
      })

      return {
        allVariables: state.allVariables.map(updateVar),
        newVariables: state.newVariables.map(updateVar),
        groups: state.groups.map(updateGroupVars),
        rootVariables: state.rootVariables.map(updateVar),
        hasChanges: true,
      }
    })

    return { variableCount: matchedLabels.size }
  },

  applyFilledCodes: (rows: ExportMapFillRow[], onlyEmpty: boolean = false) => {
    const index = buildFillRowIndex(rows)
    let variableCount = 0
    let categoryCount = 0

    // Counting happens on the allVariables pass only; groups, rootVariables
    // and newVariables hold the same variables and are re-derived for the UI.
    const fillOnly = (v: ExportMapVariable): ExportMapVariable =>
      fillVariable(v, index, onlyEmpty).variable

    const fillGroup = (group: ExportMapGroup): ExportMapGroup => ({
      ...group,
      variables: group.variables.map(fillOnly),
      subgroups: group.subgroups.map(fillGroup),
    })

    set((state) => {
      const updatedAllVariables = state.allVariables.map((v) => {
        const result = fillVariable(v, index, onlyEmpty)
        if (result.variableChanged) variableCount++
        categoryCount += result.categoryChanges
        return result.variable
      })

      if (variableCount === 0 && categoryCount === 0) {
        return state
      }

      return {
        allVariables: updatedAllVariables,
        newVariables: state.newVariables.map(fillOnly),
        groups: state.groups.map(fillGroup),
        rootVariables: state.rootVariables.map(fillOnly),
        codeToLabelsMap: buildCodeToLabelsMap(updatedAllVariables),
        hasChanges: true,
      }
    })

    return { variableCount, categoryCount }
  },

  // Max-multi
  toggleMaxMulti: (groupPath: string[]) => {
    const groupKey = groupPath.join("//")

    set((state) => {
      const newMaxMultiGroups = new Set(state.maxMultiGroups)
      const enabling = !newMaxMultiGroups.has(groupKey)

      if (enabling) {
        newMaxMultiGroups.add(groupKey)
      } else {
        newMaxMultiGroups.delete(groupKey)
      }

      // Build the set of affected labels and their codes
      const prefix = groupKey + "//"
      const codeMap = new Map<string, number | undefined>()
      let nextCode = 1
      for (const v of state.allVariables) {
        if (v.type !== "BOOL") continue
        if (!v.fullLabel.startsWith(prefix)) continue
        const remainder = v.fullLabel.slice(prefix.length)
        if (remainder.includes("//")) continue
        codeMap.set(v.fullLabel, enabling ? nextCode++ : undefined)
      }

      const updateVar = (v: ExportMapVariable): ExportMapVariable => {
        if (!codeMap.has(v.fullLabel)) return v
        const code = codeMap.get(v.fullLabel)
        if (code !== undefined) {
          return { ...v, maxMultiCode: code }
        } else {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { maxMultiCode: _, ...rest } = v
          return rest as typeof v
        }
      }

      const updateGroupVars = (group: ExportMapGroup): ExportMapGroup => ({
        ...group,
        variables: group.variables.map(updateVar),
        subgroups: group.subgroups.map(updateGroupVars),
      })

      return {
        allVariables: state.allVariables.map(updateVar),
        groups: state.groups.map(updateGroupVars),
        rootVariables: state.rootVariables.map(updateVar),
        maxMultiGroups: newMaxMultiGroups,
        hasChanges: true,
      }
    })
  },

  toggleMaxMultiAll: (enable: boolean) => {
    set((state) => {
      const newMaxMultiGroups = new Set<string>()

      // Find all groups that contain BOOL children
      const collectBoolGroups = (vars: ExportMapVariable[]) => {
        const groupKeys = new Map<string, ExportMapVariable[]>()
        for (const v of vars) {
          if (v.type !== "BOOL" || v.path.length < 2) continue
          const groupKey = v.path.slice(0, -1).join("//")
          const remainder = v.fullLabel.slice(groupKey.length + 2)
          if (remainder.includes("//")) continue
          if (!groupKeys.has(groupKey)) groupKeys.set(groupKey, [])
          groupKeys.get(groupKey)!.push(v)
        }
        return groupKeys
      }

      const boolGroups = collectBoolGroups(state.allVariables)

      // Build code assignments per group
      const codeMap = new Map<string, number | undefined>()
      for (const [groupKey, vars] of boolGroups) {
        if (enable) {
          newMaxMultiGroups.add(groupKey)
          let nextCode = 1
          for (const v of vars) {
            codeMap.set(v.fullLabel, nextCode++)
          }
        } else {
          for (const v of vars) {
            codeMap.set(v.fullLabel, undefined)
          }
        }
      }

      const updateVar = (v: ExportMapVariable): ExportMapVariable => {
        if (!codeMap.has(v.fullLabel)) return v
        const code = codeMap.get(v.fullLabel)
        if (code !== undefined) {
          return { ...v, maxMultiCode: code }
        } else {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { maxMultiCode: _, ...rest } = v
          return rest as typeof v
        }
      }

      const updateGroupVars = (group: ExportMapGroup): ExportMapGroup => ({
        ...group,
        variables: group.variables.map(updateVar),
        subgroups: group.subgroups.map(updateGroupVars),
      })

      return {
        allVariables: state.allVariables.map(updateVar),
        groups: state.groups.map(updateGroupVars),
        rootVariables: state.rootVariables.map(updateVar),
        maxMultiGroups: newMaxMultiGroups,
        hasChanges: true,
      }
    })
  },

  updateMaxMultiCode: (fullLabel: string, code: number | undefined) => {
    const updateVar = (v: ExportMapVariable): ExportMapVariable =>
      v.fullLabel === fullLabel ? { ...v, maxMultiCode: code } : v

    const updateGroupVars = (group: ExportMapGroup): ExportMapGroup => ({
      ...group,
      variables: group.variables.map(updateVar),
      subgroups: group.subgroups.map(updateGroupVars),
    })

    set((state) => ({
      allVariables: state.allVariables.map(updateVar),
      groups: state.groups.map(updateGroupVars),
      rootVariables: state.rootVariables.map(updateVar),
      hasChanges: true,
    }))
  },

  isMaxMultiGroup: (groupPath: string[]) => {
    return get().maxMultiGroups.has(groupPath.join("//"))
  },

  isAllMaxMulti: () => {
    const { allVariables, maxMultiGroups } = get()
    // Find all groups that contain BOOL children
    const boolGroupKeys = new Set<string>()
    for (const v of allVariables) {
      if (v.type !== "BOOL" || v.path.length < 2) continue
      const groupKey = v.path.slice(0, -1).join("//")
      const remainder = v.fullLabel.slice(groupKey.length + 2)
      if (!remainder.includes("//")) boolGroupKeys.add(groupKey)
    }
    if (boolGroupKeys.size === 0) return false
    for (const key of boolGroupKeys) {
      if (!maxMultiGroups.has(key)) return false
    }
    return true
  },

  getMaxMultiValidationErrors: () => {
    const { allVariables, maxMultiGroups } = get()
    const errors = new Map<string, string[]>()

    for (const groupKey of maxMultiGroups) {
      const prefix = groupKey + "//"
      const groupErrors: string[] = []
      const codes = new Map<number, string>()

      const boolChildren = allVariables.filter(
        (v) =>
          v.type === "BOOL" &&
          v.fullLabel.startsWith(prefix) &&
          !v.fullLabel.slice(prefix.length).includes("//")
      )

      for (const v of boolChildren) {
        if (v.maxMultiCode === undefined || isNaN(v.maxMultiCode)) {
          groupErrors.push(`"${v.path[v.path.length - 1]}" is missing a code`)
        } else {
          const existing = codes.get(v.maxMultiCode)
          if (existing) {
            groupErrors.push(
              `Duplicate code ${v.maxMultiCode} on "${v.path[v.path.length - 1]}" and "${existing}"`
            )
          } else {
            codes.set(v.maxMultiCode, v.path[v.path.length - 1])
          }
        }
      }

      if (groupErrors.length > 0) {
        errors.set(groupKey, groupErrors)
      }
    }

    return errors
  },

  // Reset
  reset: () => set(initialState),
}))
